import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { RequestPasswordResetDTO } from '@shared/schemas/auth';
import { requestPasswordResetSchema } from '@shared/schemas/auth';
import { getBrowserUrl, requestPasswordReset } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export function ForgotPasswordForm({
  className,
  ...props
}: React.ComponentProps<'form'>) {
  const [sentEmail, setSentEmail] = useState<string | null>(null);
  const form = useForm<RequestPasswordResetDTO>({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(requestPasswordResetSchema),
  });

  async function handleSubmit(values: RequestPasswordResetDTO) {
    form.clearErrors('root');

    const result = await requestPasswordReset(values.email, getBrowserUrl('/auth/reset-password'));

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      form.setError('root', { message });
      toast.error(message);
      return;
    }

    setSentEmail(values.email);
    toast.success('Si cet email existe, un lien de réinitialisation a été envoyé.');
  }

  return (
    <form className={cn('flex flex-col gap-6', className)} onSubmit={form.handleSubmit(handleSubmit)} {...props}>
      <FieldGroup>
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-bold">Mot de passe oublié</h1>
          <p className="text-sm text-balance text-muted-foreground">
            Renseigne ton email pour recevoir un lien de réinitialisation.
          </p>
        </div>
        <Field>
          <FieldLabel htmlFor="forgot-email">Email</FieldLabel>
          <Input
            id="forgot-email"
            data-testid="forgot-password-email"
            type="email"
            autoComplete="email"
            placeholder="nom@entreprise.fr"
            className="bg-background"
            aria-invalid={form.formState.errors.email ? 'true' : 'false'}
            {...form.register('email')}
          />
          <FieldDescription>
            Le lien envoyé te redirigera vers la page de choix du nouveau mot de passe.
          </FieldDescription>
          <FieldError errors={[form.formState.errors.email]} />
        </Field>
        <FieldError errors={[form.formState.errors.root]} />
        <Field>
          <Button data-testid="forgot-password-submit" type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? <Loader2Icon className="animate-spin" /> : null}
            Envoyer le lien
          </Button>
        </Field>
        {sentEmail ? (
          <FieldDescription data-testid="forgot-password-feedback" className="text-center">
            Si un compte existe pour {sentEmail}, l email de réinitialisation est en route.
          </FieldDescription>
        ) : null}
      </FieldGroup>
    </form>
  );
}
